import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Grid, IconButton, Menu, MenuItem } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LanguageIcon from "@material-ui/icons/Language";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import PhoneIcon from "@material-ui/icons/Phone";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import SettingsEthernetIcon from "@material-ui/icons/SettingsEthernet";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import BarChartIcon from "@material-ui/icons/BarChart";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import ScheduleIcon from "@material-ui/icons/Schedule";
import GroupIcon from "@material-ui/icons/Group";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import QueueIcon from "@material-ui/icons/Queue";
import DevicesIcon from "@material-ui/icons/Devices";
import PaymentIcon from "@material-ui/icons/Payment";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import LandingNetwork3D from "../../components/LandingNetwork3D";
import SafeThreeCanvas from "../../components/SafeThreeCanvas";
import { useLanguage } from "../../i18n/useLanguage";
import { languageNames } from "../../i18n/translations";
import LoadingScreen from "./components/LoadingScreen";
import LanguageSuggestionBanner from "../../components/LanguageSuggestionBanner";
import MobileNav from "./components/MobileNav";
import PreviewSection from "./components/PreviewSection";
import PartnersSection from "./components/PartnersSection";
import TestimonialsSection from "./components/TestimonialsSection";
import PricingSection from "./components/PricingSection";

gsap.registerPlugin(ScrollTrigger);

const CONTACT_NUMBER = "+51 927 606 818";
const CONTACT_RAW = "51927606818";
const WHATSAPP_LINK = `https://wa.me/${CONTACT_RAW}`;
const CALL_LINK = `tel:+${CONTACT_RAW}`;

const useStyles = makeStyles((theme) => ({
  "@global": {
    "html": { scrollBehavior: "smooth" },
    "::-webkit-scrollbar": { width: 0, background: "transparent" },
    "*": { scrollbarWidth: "none" },
  },
  root: {
    position: "relative",
    minHeight: "100vh",
    overflowX: "hidden",
    color: "#ffffff",
    background: "radial-gradient(circle at 74% 14%, #24506a 0%, #0e2940 34%, #081a2b 62%, #040f1a 100%)",
    fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
  },
  canvasLayer: {
    position: "fixed",
    inset: 0,
    zIndex: 0,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1,
    pointerEvents: "none",
    background:
      "linear-gradient(92deg, rgba(2, 8, 14, 0.9) 0%, rgba(2, 8, 14, 0.72) 36%, rgba(2, 8, 14, 0.28) 62%, rgba(2, 8, 14, 0.44) 100%), linear-gradient(180deg, rgba(1, 6, 12, 0.08) 0%, rgba(1, 6, 12, 0.62) 100%), radial-gradient(circle at 77% 16%, rgba(73, 236, 255, 0.18), transparent 44%)",
  },
  content: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 1280,
    margin: "0 auto",
    padding: theme.spacing(2.5, 4, 5),
    display: "flex",
    flexDirection: "column",
    gap: 0,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 2, 3),
    },
  },

  /* ─── HEADER ─── */
  header: {
    position: "sticky",
    top: theme.spacing(1.5),
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.8),
    borderRadius: 60,
    border: "1px solid rgba(90, 205, 228, 0.24)",
    background: "linear-gradient(135deg, rgba(7, 26, 39, 0.74), rgba(6, 22, 34, 0.54))",
    backdropFilter: "blur(20px) saturate(1.4)",
    boxShadow: "0 12px 34px rgba(2, 8, 12, 0.45)",
  },
  brand: {
    minWidth: 170,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: "#ffffff",
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    fontSize: "0.95rem",
  },
  brandDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: "linear-gradient(130deg, #58EFFE, #39F3CC)",
    boxShadow: "0 0 20px rgba(88, 239, 254, 0.5), 0 0 40px rgba(57, 243, 204, 0.2)",
    flex: "0 0 auto",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.3),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  navLink: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.88rem",
    color: "rgba(219, 240, 247, 0.84)",
    borderRadius: 30,
    padding: theme.spacing(0.6, 1.4),
    transition: "all 0.3s ease",
    "&:hover": {
      color: "#ffffff",
      background: "rgba(88, 239, 254, 0.14)",
    },
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.6),
  },
  languageButton: {
    color: "rgba(255, 255, 255, 0.7)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    background: "rgba(255, 255, 255, 0.05)",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.1)",
    },
  },
  loginButton: {
    textTransform: "none",
    fontWeight: 700,
    color: "#000000",
    borderRadius: 30,
    padding: theme.spacing(0.8, 2),
    fontSize: "0.85rem",
    background: "linear-gradient(135deg, #58EFFE 0%, #39F3CC 100%)",
    boxShadow: "0 4px 20px rgba(88, 239, 254, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "linear-gradient(135deg, #3dd4e8 0%, #2dd8b0 100%)",
      boxShadow: "0 6px 28px rgba(88, 239, 254, 0.45)",
      transform: "translateY(-1px)",
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: 42,
      padding: theme.spacing(0.8),
      "& span": {
        display: "none",
      },
    },
  },

  /* ─── HERO ─── */
  hero: {
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(6),
    [theme.breakpoints.down("md")]: {
      minHeight: "90vh",
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: "85vh",
      alignItems: "flex-end",
      paddingBottom: theme.spacing(5),
    },
  },
  heroContent: {
    position: "relative",
    zIndex: 3,
    maxWidth: 640,
    width: "55%",
    padding: theme.spacing(3, 3, 3, 0),
    "&::before": {
      content: '""',
      position: "absolute",
      inset: "-44px -24px -48px -70px",
      background: "radial-gradient(ellipse at 30% 52%, rgba(4, 14, 22, 0.86), rgba(4, 14, 22, 0.44) 52%, transparent 78%)",
      pointerEvents: "none",
      zIndex: -1,
    },
    [theme.breakpoints.down("md")]: {
      width: "65%",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(0.5, 1.4),
    borderRadius: 999,
    border: "1px solid rgba(88, 239, 254, 0.3)",
    background: "rgba(88, 239, 254, 0.06)",
    color: "#58EFFE",
    fontSize: "0.76rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    backdropFilter: "blur(10px)",
  },
  badgeDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#39F3CC",
    boxShadow: "0 0 8px rgba(57, 243, 204, 0.6)",
  },
  title: {
    margin: 0,
    color: "#ffffff",
    lineHeight: 1.0,
    letterSpacing: "-0.03em",
    fontSize: "clamp(2.6rem, 6.5vw, 6.2rem)",
    fontWeight: 800,
    maxWidth: 640,
    textShadow: "0 6px 28px rgba(3, 11, 18, 0.65), 0 0 22px rgba(5, 16, 26, 0.48)",
  },
  titleAccent: {
    background: "linear-gradient(135deg, #58EFFE 0%, #39F3CC 50%, #9081ff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  subtitle: {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    maxWidth: 520,
    color: "rgba(229, 243, 249, 0.9)",
    fontSize: "clamp(1rem, 1.8vw, 1.25rem)",
    lineHeight: 1.6,
    fontWeight: 400,
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.2),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroPrimary: {
    textTransform: "none",
    fontWeight: 700,
    color: "#000000",
    borderRadius: 999,
    padding: theme.spacing(1.2, 3),
    fontSize: "1rem",
    background: "linear-gradient(135deg, #58EFFE 0%, #39F3CC 100%)",
    boxShadow: "0 4px 24px rgba(88, 239, 254, 0.35)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "linear-gradient(135deg, #3dd4e8 0%, #2dd8b0 100%)",
      boxShadow: "0 8px 36px rgba(88, 239, 254, 0.5)",
      transform: "translateY(-2px)",
    },
  },
  heroSecondary: {
    textTransform: "none",
    fontWeight: 600,
    color: "rgba(255, 255, 255, 0.8)",
    borderRadius: 999,
    border: "1px solid rgba(255, 255, 255, 0.15)",
    background: "rgba(255, 255, 255, 0.04)",
    padding: theme.spacing(1.1, 2.8),
    fontSize: "1rem",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.1)",
      borderColor: "rgba(255, 255, 255, 0.3)",
      color: "#ffffff",
    },
  },
  scrollIndicator: {
    position: "absolute",
    left: "50%",
    bottom: theme.spacing(3),
    transform: "translateX(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    textDecoration: "none",
    color: "rgba(255, 255, 255, 0.4)",
    fontSize: "0.75rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontWeight: 600,
    animation: "float-indicator 3s ease-in-out infinite",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  scrollLine: {
    width: 1,
    height: 40,
    background: "linear-gradient(180deg, rgba(88, 239, 254, 0.5), transparent)",
  },

  /* ─── SECTIONS ─── */
  sectionSpacer: {
    height: theme.spacing(10),
    [theme.breakpoints.down("sm")]: {
      height: theme.spacing(6),
    },
  },
  section: {
    borderRadius: 24,
    border: "1px solid rgba(88, 239, 254, 0.18)",
    background: "linear-gradient(145deg, rgba(6, 25, 39, 0.74), rgba(3, 18, 30, 0.62))",
    backdropFilter: "blur(16px) saturate(1.3)",
    padding: theme.spacing(4, 3.5),
    boxShadow: "0 24px 52px rgba(2, 9, 15, 0.46)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2.5, 2),
      borderRadius: 18,
    },
  },
  sectionLabel: {
    margin: 0,
    marginBottom: theme.spacing(1),
    color: "#74e7ff",
    fontSize: "0.76rem",
    fontWeight: 700,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
  },
  sectionTitle: {
    margin: 0,
    color: "#ffffff",
    fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)",
    fontWeight: 800,
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
  },
  sectionSubtitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    color: "rgba(221, 236, 244, 0.78)",
    lineHeight: 1.6,
    maxWidth: 680,
    fontSize: "1rem",
  },
  servicesTitle: {
    marginTop: 0,
    marginBottom: theme.spacing(1.5),
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.9rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    height: 1,
    background: "linear-gradient(90deg, transparent, rgba(88, 239, 254, 0.2), transparent)",
  },

  /* ─── CARDS ─── */
  card: {
    borderRadius: 16,
    border: "1px solid rgba(128, 211, 228, 0.2)",
    background: "linear-gradient(140deg, rgba(8, 29, 45, 0.76), rgba(5, 22, 37, 0.64))",
    backdropFilter: "blur(8px)",
    padding: theme.spacing(2),
    minHeight: 150,
    transition: "all 0.35s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      borderColor: "rgba(104, 234, 255, 0.38)",
      background: "linear-gradient(140deg, rgba(10, 36, 55, 0.84), rgba(7, 29, 45, 0.74))",
      boxShadow: "0 18px 44px rgba(2, 9, 14, 0.42), 0 0 28px rgba(88, 239, 254, 0.1)",
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
  },
  cardIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(1.2),
    background: "linear-gradient(135deg, rgba(88, 239, 254, 0.12), rgba(57, 243, 204, 0.06))",
    color: "#58EFFE",
    boxShadow: "0 0 20px rgba(88, 239, 254, 0.08)",
  },
  cardTitle: {
    margin: 0,
    marginBottom: theme.spacing(0.6),
    color: "#ffffff",
    fontSize: "1.05rem",
    fontWeight: 700,
  },
  cardDescription: {
    margin: 0,
    color: "rgba(219, 236, 244, 0.84)",
    lineHeight: 1.55,
    fontSize: "0.92rem",
  },
  marketCta: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid rgba(255, 255, 255, 0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  marketHint: {
    margin: 0,
    color: "rgba(255, 255, 255, 0.5)",
    lineHeight: 1.5,
    fontSize: "0.95rem",
  },
  marketButton: {
    textTransform: "none",
    fontWeight: 700,
    color: "#000000",
    borderRadius: 999,
    padding: theme.spacing(1, 2.2),
    background: "linear-gradient(135deg, #58EFFE 0%, #39F3CC 100%)",
    boxShadow: "0 4px 20px rgba(88, 239, 254, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "linear-gradient(135deg, #3dd4e8 0%, #2dd8b0 100%)",
      boxShadow: "0 6px 28px rgba(88, 239, 254, 0.45)",
      transform: "translateY(-1px)",
    },
  },

  /* ─── CONTACT ─── */
  contactWrap: {
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 420px)",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  contactInfo: {
    borderRadius: 24,
    border: "1px solid rgba(88, 239, 254, 0.2)",
    background: "linear-gradient(145deg, rgba(6, 25, 39, 0.8), rgba(4, 19, 30, 0.65))",
    backdropFilter: "blur(16px) saturate(1.3)",
    padding: theme.spacing(3),
  },
  contactTitle: {
    marginTop: 0,
    marginBottom: theme.spacing(1),
    color: "#ffffff",
    fontSize: "1.4rem",
    fontWeight: 800,
  },
  contactText: {
    margin: 0,
    color: "rgba(255, 255, 255, 0.5)",
    lineHeight: 1.6,
  },
  contactPanel: {
    borderRadius: 24,
    border: "1px solid rgba(88, 239, 254, 0.24)",
    background: "linear-gradient(145deg, rgba(7, 27, 42, 0.82), rgba(5, 21, 34, 0.7))",
    backdropFilter: "blur(16px) saturate(1.3)",
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    boxShadow: "0 16px 40px rgba(0, 0, 0, 0.3)",
  },
  actionButton: {
    width: "100%",
    justifyContent: "flex-start",
    textTransform: "none",
    borderRadius: 14,
    fontWeight: 700,
    padding: theme.spacing(1.1, 1.5),
    transition: "all 0.3s ease",
  },
  actionPrimary: {
    color: "#000000",
    background: "linear-gradient(135deg, #58EFFE 0%, #39F3CC 100%)",
    boxShadow: "0 4px 16px rgba(88, 239, 254, 0.25)",
    "&:hover": {
      background: "linear-gradient(135deg, #3dd4e8 0%, #2dd8b0 100%)",
      boxShadow: "0 6px 24px rgba(88, 239, 254, 0.4)",
      transform: "translateY(-1px)",
    },
  },
  actionSecondary: {
    color: "rgba(255, 255, 255, 0.8)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    background: "rgba(255, 255, 255, 0.04)",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.08)",
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
  },
  contactLine: {
    marginTop: theme.spacing(1),
    marginBottom: 0,
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1rem",
  },
  hint: {
    marginTop: theme.spacing(0.5),
    marginBottom: 0,
    color: "rgba(255, 255, 255, 0.4)",
    lineHeight: 1.5,
    fontSize: "0.85rem",
  },

  /* ─── FOOTER ─── */
  footer: {
    marginTop: theme.spacing(8),
    borderRadius: 20,
    border: "1px solid rgba(88, 239, 254, 0.16)",
    background: "linear-gradient(145deg, rgba(5, 22, 35, 0.76), rgba(4, 17, 28, 0.68))",
    backdropFilter: "blur(16px) saturate(1.3)",
    padding: theme.spacing(2.5, 2.5),
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "1.2fr 0.8fr 1fr",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  footerBlock: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.6),
  },
  footerBrand: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    color: "#ffffff",
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    fontSize: "0.9rem",
  },
  footerText: {
    margin: 0,
    color: "rgba(255, 255, 255, 0.35)",
    fontSize: "0.78rem",
    lineHeight: 1.5,
  },
  footerNav: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  footerLink: {
    textTransform: "none",
    color: "rgba(255, 255, 255, 0.5)",
    borderColor: "rgba(255, 255, 255, 0.08)",
    borderRadius: 10,
    padding: theme.spacing(0.4, 1),
    fontSize: "0.78rem",
    fontWeight: 600,
    minWidth: 0,
    "&:hover": {
      color: "#ffffff",
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
  },
  footerActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  footerPrimary: {
    textTransform: "none",
    fontWeight: 700,
    color: "#000000",
    borderRadius: 10,
    padding: theme.spacing(0.45, 1.1),
    minWidth: 0,
    fontSize: "0.78rem",
    background: "linear-gradient(135deg, #58EFFE 0%, #39F3CC 100%)",
    "&:hover": {
      background: "linear-gradient(135deg, #3dd4e8 0%, #2dd8b0 100%)",
    },
  },
  footerSecondary: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    padding: theme.spacing(0.45, 1.1),
    minWidth: 0,
    fontSize: "0.78rem",
    color: "rgba(255, 255, 255, 0.5)",
    borderColor: "rgba(255, 255, 255, 0.08)",
    "&:hover": {
      color: "#ffffff",
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
  },
}));

const SERVICE_TONES = [
  "linear-gradient(145deg, rgba(16, 54, 75, 0.78), rgba(8, 31, 47, 0.64))",
  "linear-gradient(145deg, rgba(14, 60, 62, 0.78), rgba(8, 34, 41, 0.64))",
  "linear-gradient(145deg, rgba(22, 52, 92, 0.78), rgba(10, 28, 56, 0.64))",
  "linear-gradient(145deg, rgba(19, 62, 70, 0.78), rgba(10, 33, 40, 0.64))",
];

const BENEFIT_TONES = [
  "linear-gradient(145deg, rgba(18, 62, 82, 0.8), rgba(9, 33, 49, 0.66))",
  "linear-gradient(145deg, rgba(24, 67, 71, 0.8), rgba(10, 35, 41, 0.66))",
  "linear-gradient(145deg, rgba(21, 58, 100, 0.8), rgba(10, 30, 61, 0.66))",
];

const MARKET_TONES = [
  "linear-gradient(145deg, rgba(17, 58, 82, 0.8), rgba(9, 31, 50, 0.66))",
  "linear-gradient(145deg, rgba(11, 61, 70, 0.8), rgba(8, 35, 42, 0.66))",
  "linear-gradient(145deg, rgba(20, 54, 94, 0.8), rgba(9, 27, 58, 0.66))",
];

const serviceIconMap = {
  omnichannel: SettingsEthernetIcon,
  automation: FlashOnIcon,
  analytics: BarChartIcon,
  support: AssignmentTurnedInIcon,
};

const services = [
  { id: "omnichannel", titleKey: "landing.services.omnichannelTitle", descriptionKey: "landing.services.omnichannelDescription" },
  { id: "automation", titleKey: "landing.services.automationTitle", descriptionKey: "landing.services.automationDescription" },
  { id: "analytics", titleKey: "landing.services.analyticsTitle", descriptionKey: "landing.services.analyticsDescription" },
  { id: "support", titleKey: "landing.services.supportTitle", descriptionKey: "landing.services.supportDescription" },
];

const benefitIconMap = {
  centralized: SettingsEthernetIcon,
  response: ScheduleIcon,
  followup: AssignmentTurnedInIcon,
  visibility: BarChartIcon,
  teamwork: GroupIcon,
  governance: CheckCircleIcon,
};

const benefits = [
  { id: "centralized", titleKey: "landing.benefits.centralizedTitle", descriptionKey: "landing.benefits.centralizedDescription" },
  { id: "response", titleKey: "landing.benefits.responseTitle", descriptionKey: "landing.benefits.responseDescription" },
  { id: "followup", titleKey: "landing.benefits.followupTitle", descriptionKey: "landing.benefits.followupDescription" },
  { id: "visibility", titleKey: "landing.benefits.visibilityTitle", descriptionKey: "landing.benefits.visibilityDescription" },
  { id: "teamwork", titleKey: "landing.benefits.teamworkTitle", descriptionKey: "landing.benefits.teamworkDescription" },
  { id: "governance", titleKey: "landing.benefits.governanceTitle", descriptionKey: "landing.benefits.governanceDescription" },
];

const marketIconMap = {
  automation: QueueIcon,
  ai: FlashOnIcon,
  integrations: DevicesIcon,
  billing: PaymentIcon,
};

const marketZones = [
  { id: "automation", titleKey: "landing.market.items.automationTitle", descriptionKey: "landing.market.items.automationDescription" },
  { id: "ai", titleKey: "landing.market.items.aiTitle", descriptionKey: "landing.market.items.aiDescription" },
  { id: "integrations", titleKey: "landing.market.items.integrationsTitle", descriptionKey: "landing.market.items.integrationsDescription" },
  { id: "billing", titleKey: "landing.market.items.billingTitle", descriptionKey: "landing.market.items.billingDescription" },
];

const navItems = [
  { id: "benefits", labelKey: "landing.nav.benefits" },
  { id: "market", labelKey: "landing.nav.market" },
  { id: "pricing", labelKey: "landing.nav.pricing" },
  { id: "contact", labelKey: "landing.nav.contact" },
];

const floatKeyframes = `
@keyframes float-indicator {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(-8px); }
}`;

export default function Landing() {
  const classes = useStyles();
  const history = useHistory();
  const { language, setLanguage, selectLanguage, suggestedByIP, t } = useLanguage();
  const [languageAnchor, setLanguageAnchor] = useState(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const contentRef = useRef(null);
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const resolveText = (path) =>
    path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), t) || path;

  const activeLanguage = useMemo(() => (languageNames[language] ? language : "es"), [language]);

  const handleGoToLogin = () => {
    history.push("/login");
  };

  useEffect(() => {
    const timer = setTimeout(() => setCanvasReady(true), 1800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;
    const sections = contentRef.current.querySelectorAll(".gsap-fade-in");
    const cards = contentRef.current.querySelectorAll(".gsap-card");

    sections.forEach((el) => {
      gsap.fromTo(el,
        { opacity: 0, y: 60 },
        { opacity: 1, y: 0, duration: 1.0, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" }
        }
      );
    });

    cards.forEach((el, i) => {
      gsap.fromTo(el,
        { opacity: 0, y: 40, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, delay: i * 0.06, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" }
        }
      );
    });

    return () => ScrollTrigger.getAll().forEach((st) => st.kill());
  }, []);

  const footerRights = resolveText("landing.footer.rights").replace("{{year}}", `${currentYear}`);
  const contactLabel = resolveText("landing.contactLabel").replace("{{number}}", CONTACT_NUMBER);

  return (
    <div className={classes.root}>
      <style>{floatKeyframes}</style>
      {/* LoadingScreen removed by Datxale 20260429 */}
      <LanguageSuggestionBanner currentLang={language} suggested={suggestedByIP} onAccept={(l) => (selectLanguage || setLanguage)(l)} />

      <div className={classes.canvasLayer}>
        <SafeThreeCanvas fallback={null}>
          <LandingNetwork3D />
        </SafeThreeCanvas>
      </div>
      <div className={classes.overlay} />

      <div className={classes.content} ref={contentRef}>
        <header className={classes.header}>
          <div className={classes.brand}>
            <img src="/logo.svg" alt="Klorum" style={{width:34,height:34,marginRight:10}} />
            <span>Klorum</span>
          </div>

          <nav className={classes.nav}>
            {navItems.map((item) => (
              <Button key={item.id} className={classes.navLink} component="a" href={`#${item.id}`}>
                {resolveText(item.labelKey)}
              </Button>
            ))}
          </nav>

          <div className={classes.headerActions}>
            <IconButton className={classes.languageButton} size="small" aria-label="language" onClick={(event) => setLanguageAnchor(event.currentTarget)}>
              <LanguageIcon fontSize="small" />
            </IconButton>
            <MobileNav navItems={navItems} resolveText={resolveText} onLogin={handleGoToLogin} />
            <Button className={classes.loginButton} startIcon={<ExitToAppIcon fontSize="small" />} onClick={handleGoToLogin}>
              <span>{resolveText("landing.actions.login")}</span>
            </Button>
          </div>
        </header>

        <section className={classes.hero}>
          <div className={classes.heroContent}>
            <span className={classes.badge}>
              <span className={classes.badgeDot} />
              {resolveText("landing.badge")}
            </span>
            <h1 className={classes.title}>
              {resolveText("landing.title")}
            </h1>
            <p className={classes.subtitle}>{resolveText("landing.subtitle")}</p>

            <div className={classes.heroActions}>
              <Button className={classes.heroPrimary} startIcon={<ExitToAppIcon fontSize="small" />} onClick={handleGoToLogin}>
                {resolveText("landing.actions.login")}
              </Button>
              <Button className={classes.heroSecondary} component="a" href="#market">
                {resolveText("landing.nav.market")}
              </Button>
            </div>
          </div>

          <a className={classes.scrollIndicator} href="#benefits" aria-label={resolveText("landing.nav.benefits")}>
            <span className={classes.scrollLine} />
            <KeyboardArrowDownIcon fontSize="small" />
          </a>
        </section>

        <PreviewSection resolveText={resolveText} />

        <div className={classes.sectionSpacer} />

        <section className={`${classes.section} gsap-fade-in`} id="benefits">
          <p className={classes.sectionLabel}>{resolveText("landing.servicesTitle")}</p>
          <h2 className={classes.sectionTitle}>{resolveText("landing.benefitsTitle")}</h2>
          <p className={classes.sectionSubtitle}>{resolveText("landing.benefitsSubtitle")}</p>

          <Grid container spacing={2}>
            {services.map((service, index) => {
              const IconComponent = serviceIconMap[service.id];
              return (
                <Grid item xs={12} sm={6} key={service.id}>
                  <div className={`${classes.card} gsap-card`} style={{ background: SERVICE_TONES[index % SERVICE_TONES.length] }}>
                    <div className={classes.cardIcon}><IconComponent fontSize="small" /></div>
                    <h3 className={classes.cardTitle}>{resolveText(service.titleKey)}</h3>
                    <p className={classes.cardDescription}>{resolveText(service.descriptionKey)}</p>
                  </div>
                </Grid>
              );
            })}
          </Grid>

          <div className={classes.divider} />

          <Grid container spacing={2}>
            {benefits.map((benefit, index) => {
              const IconComponent = benefitIconMap[benefit.id];
              return (
                <Grid item xs={12} sm={6} md={4} key={benefit.id}>
                  <div className={`${classes.card} gsap-card`} style={{ background: BENEFIT_TONES[index % BENEFIT_TONES.length] }}>
                    <div className={classes.cardIcon}><IconComponent fontSize="small" /></div>
                    <h3 className={classes.cardTitle}>{resolveText(benefit.titleKey)}</h3>
                    <p className={classes.cardDescription}>{resolveText(benefit.descriptionKey)}</p>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </section>

        <div className={classes.sectionSpacer} />

        <PartnersSection resolveText={resolveText} />

        <div className={classes.sectionSpacer} />

        <section className={`${classes.section} gsap-fade-in`} id="market">
          <p className={classes.sectionLabel}>{resolveText("landing.nav.market")}</p>
          <h2 className={classes.sectionTitle}>{resolveText("landing.market.title")}</h2>
          <p className={classes.sectionSubtitle}>{resolveText("landing.market.subtitle")}</p>

          <Grid container spacing={2}>
            {marketZones.map((zone, index) => {
              const IconComponent = marketIconMap[zone.id];
              return (
                <Grid item xs={12} sm={6} key={zone.id}>
                  <div className={`${classes.card} gsap-card`} style={{ background: MARKET_TONES[index % MARKET_TONES.length] }}>
                    <div className={classes.cardIcon}><IconComponent fontSize="small" /></div>
                    <h3 className={classes.cardTitle}>{resolveText(zone.titleKey)}</h3>
                    <p className={classes.cardDescription}>{resolveText(zone.descriptionKey)}</p>
                  </div>
                </Grid>
              );
            })}
          </Grid>

          <div className={classes.marketCta}>
            <p className={classes.marketHint}>{resolveText("landing.market.ctaHint")}</p>
            <Button className={classes.marketButton} startIcon={<ExitToAppIcon fontSize="small" />} onClick={handleGoToLogin}>
              {resolveText("landing.market.ctaButton")}
            </Button>
          </div>
        </section>

        <div className={classes.sectionSpacer} />

        <TestimonialsSection resolveText={resolveText} />

        <div className={classes.sectionSpacer} />

        <PricingSection resolveText={resolveText} onLogin={handleGoToLogin} />

        <div className={classes.sectionSpacer} />

        <section className={`${classes.contactWrap} gsap-fade-in`} id="contact">
          <article className={classes.contactInfo}>
            <h2 className={classes.contactTitle}>{contactLabel}</h2>
            <p className={classes.contactText}>{resolveText("landing.redirectHint")}</p>
            <p className={classes.hint}>{resolveText("landing.footer.note")}</p>
          </article>

          <aside className={classes.contactPanel}>
            <Button className={`${classes.actionButton} ${classes.actionPrimary}`} startIcon={<ExitToAppIcon fontSize="small" />} onClick={handleGoToLogin}>
              {resolveText("landing.actions.login")}
            </Button>
            <Button className={`${classes.actionButton} ${classes.actionSecondary}`} startIcon={<WhatsAppIcon fontSize="small" />} component="a" href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              {resolveText("landing.actions.whatsapp")}
            </Button>
            <Button className={`${classes.actionButton} ${classes.actionSecondary}`} startIcon={<PhoneIcon fontSize="small" />} component="a" href={CALL_LINK}>
              {resolveText("landing.actions.call")}
            </Button>
            <p className={classes.contactLine}>{contactLabel}</p>
            <p className={classes.hint}>{resolveText("landing.redirectHint")}</p>
          </aside>
        </section>

        <footer className={classes.footer}>
          <div className={classes.footerBlock}>
            <div className={classes.footerBrand}><img src="/logo.svg" alt="Klorum" style={{width:26,height:26,marginRight:8}} /><span>Klorum</span></div>
            <p className={classes.footerText}>{footerRights}</p>
            <p className={classes.footerText}>{resolveText("landing.footer.note")}</p>
          </div>

          <div className={classes.footerBlock}>
            <div className={classes.footerNav}>
              <Button className={classes.footerLink} variant="outlined" component="a" href="#benefits">{resolveText("landing.nav.benefits")}</Button>
              <Button className={classes.footerLink} variant="outlined" component="a" href="#market">{resolveText("landing.nav.market")}</Button>
              <Button className={classes.footerLink} variant="outlined" component="a" href="#contact">{resolveText("landing.nav.contact")}</Button>
            </div>
          </div>

          <div className={classes.footerBlock}>
            <div className={classes.footerActions}>
              <Button className={classes.footerPrimary} startIcon={<ExitToAppIcon fontSize="small" />} onClick={handleGoToLogin}>
                {resolveText("landing.actions.login")}
              </Button>
              <Button className={classes.footerSecondary} variant="outlined" startIcon={<WhatsAppIcon fontSize="small" />} component="a" href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                {resolveText("landing.actions.whatsapp")}
              </Button>
              <Button className={classes.footerSecondary} variant="outlined" startIcon={<PhoneIcon fontSize="small" />} component="a" href={CALL_LINK}>
                {resolveText("landing.actions.call")}
              </Button>
            </div>
          </div>
        </footer>
      </div>

      <Menu anchorEl={languageAnchor} open={Boolean(languageAnchor)} onClose={() => setLanguageAnchor(null)}>
        {Object.entries(languageNames).map(([code, label]) => (
          <MenuItem
            key={code}
            selected={activeLanguage === code}
            onClick={() => {
              setLanguage(code);
              setLanguageAnchor(null);
            }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
